import React, { createContext, useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as userService from "@/services/userService";
import * as authService from "@/services/authService";
import { AUTH_CONFIG } from "@/config";
import { UserProfile } from "@/types";

interface User {
  id: string;
  email: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  userRole: string | null;
  hasActiveSubscription: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string, inviteCode: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  isAdmin: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const roleRequiresSubscription = (role: string | null) => {
  if (!role) return true;
  const normalized = role.toLowerCase();
  return normalized === "listener" || normalized === "user";
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const updateSubscriptionState = (profile: UserProfile | null) => {
    const subscribed = Boolean(
      profile?.is_subscribed ??
      profile?.has_active_subscription ??
      false
    );
    setHasActiveSubscription(subscribed);
    return subscribed;
  };

  const fetchProfile = async () => {
    try {
      const profile = await userService.getUserProfile();
      updateSubscriptionState(profile);
      return profile;
    } catch (error) {
      console.error("Error fetching user profile:", error);
      setHasActiveSubscription(false);
      return null;
    }
  };

  // ✅ Fetch session and user data on mount
  useEffect(() => {
    const checkSession = async () => {
      setLoading(true);
      try {
        // Use authService for session check
        const sessionData = await authService.getSession();
        if (sessionData && sessionData.session && sessionData.user) {
          const { session, user } = sessionData;
          
          setToken(session.access_token);
          setUser(user);
          localStorage.setItem(AUTH_CONFIG.tokenStorageKey, session.access_token);

          // Use userService only for role check
          const roleResponse = await userService.getUserRole(session.access_token);
          setUserRole(roleResponse?.role || null);

          await fetchProfile();
        } else {
          handleLogout();
        }
      } catch (error) {
        console.error("Error checking session:", error);
        handleLogout();
      } finally {
        setLoading(false);
      }
    };

    checkSession();
    const refreshInterval = setInterval(checkSession, 5 * 60 * 1000);
    return () => clearInterval(refreshInterval);
  }, []);

  // ✅ Login
  const login = async (email: string, password: string) => {
    try {
      // Detect iOS without using window.MSStream
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      
      console.log(`Attempting login${isIOS ? ' on iOS device' : ''}`);
      
      // Try localStorage access early to detect issues
      try {
        localStorage.setItem('test-localStorage', 'test');
        localStorage.removeItem('test-localStorage');
      } catch (storageError) {
        console.error('LocalStorage access error:', storageError);
        throw new Error('Browser storage is restricted. Please ensure cookies are enabled and you are not in private browsing mode.');
      }
      
      const { user, session } = await authService.login(email, password);
      
      setUser(user);
      
      // Use try-catch for localStorage operations to handle iOS restrictions
      try {
        setToken(session.access_token);
        localStorage.setItem(AUTH_CONFIG.tokenStorageKey, session.access_token);
      } catch (storageError) {
        console.error('Token storage error:', storageError);
        // Continue anyway - we might still succeed with cookies
      }

      // Use userService only for role check
      const roleResponse = await userService.getUserRole(session.access_token);
      setUserRole(roleResponse?.role || null);

      const profile = await fetchProfile();
      const subscribed = updateSubscriptionState(profile);
      const role = roleResponse?.role || null;
      const requiresSubscription = roleRequiresSubscription(role);
      
      // Determine destination based on role and subscription status
      // Admins, superadmins, and artists bypass subscription requirement
      let destination: string;
      if (!requiresSubscription) {
        // Role doesn't require subscription (admin, artist, etc.) - go to discover
        destination = AUTH_CONFIG.loginRedirectPath;
      } else if (subscribed) {
        // User requires subscription and has one - go to discover
        destination = AUTH_CONFIG.loginRedirectPath;
      } else {
        // User requires subscription but doesn't have one - go to subscribe page
        destination = "/subscribe";
      }

      console.log(`🔀 Login redirect: role=${role}, requiresSubscription=${requiresSubscription}, subscribed=${subscribed}, destination=${destination}`);
      navigate(destination, { replace: true });
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  };

  // ✅ Register
  const register = async (email: string, password: string, displayName: string, inviteCode: string) => {
    try {
      await authService.register(email, password, displayName, inviteCode);
      await login(email, password);
    } catch (error) {
      console.error("Registration error:", error);
      throw error;
    }
  };

  const handleLogout = () => {
    setUser(null);
    setToken(null);
    setUserRole(null);
    setHasActiveSubscription(false);
    localStorage.removeItem(AUTH_CONFIG.tokenStorageKey);
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      handleLogout();
      navigate(AUTH_CONFIG.logoutRedirectPath);
    }
  };

  const isAdmin = () => userRole === "admin";

  const refreshProfile = async () => {
    if (!token) {
      setHasActiveSubscription(false);
      return;
    }
    await fetchProfile();
  };

  const value = { user, token, loading, userRole, hasActiveSubscription, login, register, logout, refreshProfile, isAdmin };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthProvider;


















